import { z } from 'zod';

import { CRYPTO_CREATE_TIMEOUT_MS } from '~/domains/payments/deposits/components/crypto-direct/constants';
import { objectToTuples } from '~/services/utils';
import type {
  VoucherResponseDto,
  VoucherEntryType,
} from '~/domains/promotions/types/voucher.types';

import type {
  IGetResponsibleGamingLimitsResponse,
  PostResponsibleGamingLimitsPayload,
  PostResponsibleGamingTimeoutPayload,
  IGetTransactionHistoryResponse,
} from './interfaces/responsible-gaming.interface';
import type {
  IGetSettingsAvailablePaymentMethodsResponse,
  IGetSettingsFeesResponse,
  IGetSettingsTransactionLimitsResponse,
} from './interfaces/settings.interface';
import type {
  BankAccount,
  IGetWalletsBalance,
  IGetWalletsPaymentHandles,
  IGetWalletsRefundableBreakdown,
  IGetVouchersResponse,
  IPostWalletsDepositsPayload,
  IPostWalletsDepositsResponse,
  IPostWalletsPaymentsHandlesPaypalPayload,
  IPostWalletsPaymentsHandlesPaypalResponse,
  IPostWalletsRefundsPayload,
  IPostWalletsRefundsResponse,
} from './interfaces/wallets.interface';
import { parseCryptoDepositAddresses, parseCryptoDepositArrivals } from './crypto-direct.schema';
import { parseWithdrawalEligibility } from './withdrawal-eligibility.schema';
import {
  CRYPTO_ADDRESSES_API_VERSION,
  CRYPTO_V2_API_VERSION,
  CRYPTO_V2_API_VERSION_HEADER,
  CryptoCooldownError,
  type ICryptoDepositAddresses,
  type ICryptoDepositArrivals,
} from './interfaces/crypto-direct.interface';
import { walletServiceInstance } from './wallet.service.instance';

export const getWalletBalance = async () => {
  const response = await walletServiceInstance.get<IGetWalletsBalance>('/wallets/balance');
  return response.data;
};

export const getWalletRefundableBreakdown = async () => {
  const response = await walletServiceInstance.get<IGetWalletsRefundableBreakdown>(
    '/wallets/refundable-breakdown'
  );
  return response.data;
};

export const getWithdrawalEligibility = async (amountCents: number) => {
  const response = await walletServiceInstance.get<unknown>('/wallets/withdrawal-eligibility', {
    params: { amountCents },
  });
  return parseWithdrawalEligibility(response.data);
};

export const withdrawFromWallet = async (payload: Record<string, unknown>) => {
  const response = await walletServiceInstance.post('/wallets/withdrawals', payload);
  return response.data;
};

export const depositToWallet = async (payload: IPostWalletsDepositsPayload) => {
  const response = await walletServiceInstance.post<IPostWalletsDepositsResponse>(
    '/wallets/deposits',
    payload
  );
  return response.data;
};

export const createPayPalHandle = async (payload: IPostWalletsPaymentsHandlesPaypalPayload) => {
  const response = await walletServiceInstance.post<IPostWalletsPaymentsHandlesPaypalResponse>(
    '/wallets/payment-handles/paypal',
    payload
  );
  return response.data;
};

export const createSkrillHandle = async (payload: IPostWalletsPaymentsHandlesPaypalPayload) => {
  const response = await walletServiceInstance.post<IPostWalletsPaymentsHandlesPaypalResponse>(
    '/wallets/payment-handles/skrill',
    payload
  );
  return response.data;
};

export const createVenmoHandle = async (payload: IPostWalletsPaymentsHandlesPaypalPayload) => {
  const response = await walletServiceInstance.post<IPostWalletsPaymentsHandlesPaypalResponse>(
    '/wallets/payment-handles/venmo',
    payload
  );
  return response.data;
};

export const getPaymentsMethods = async () => {
  const response =
    await walletServiceInstance.get<IGetWalletsPaymentHandles>('wallets/payment-handles');
  return response.data;
};

export const getTransactionHistory = async ({
  page,
  limit = 10,
  from,
  to,
}: {
  page: number;
  limit?: number;
  from?: Date;
  to?: Date;
}) => {
  const payload = {
    page,
    pageSize: limit,
    ...(from && { from: from.toISOString() }),
    ...(to && { to: to.toISOString() }),
  };

  const query = new URLSearchParams(objectToTuples(payload)).toString();
  const response = await walletServiceInstance.get<IGetTransactionHistoryResponse>(
    `ledger/transactions?${query}`
  );
  return response.data;
};

export const getResponsibleGamingLimits = async () => {
  const response = await walletServiceInstance.get<IGetResponsibleGamingLimitsResponse>(`limits`);
  return response.data;
};

export const postResponsibleGamingLimits = async (payload: PostResponsibleGamingLimitsPayload) => {
  const response = await walletServiceInstance.post(`limits`, payload);
  return response.data;
};

export const postResponsibleGamingTimeout = async (
  payload: PostResponsibleGamingTimeoutPayload
) => {
  const response = await walletServiceInstance.post(`timeouts`, payload);
  return response.data.data;
};

export const getTransactionLimits = async () => {
  const response = await walletServiceInstance.get<IGetSettingsTransactionLimitsResponse>(
    'settings/transaction-limits'
  );
  return response.data.data;
};

export const getProcessingFees = async () => {
  const response = await walletServiceInstance.get<IGetSettingsFeesResponse>('settings/fees');
  return response.data.data;
};

export const getAvailablePaymentMethodsDeposit = async () => {
  const response = await walletServiceInstance.get<IGetSettingsAvailablePaymentMethodsResponse>(
    'settings/available-payment-methods/deposit'
  );
  return response.data.data;
};

export const getAvailablePaymentMethodsWithdrawal = async () => {
  const response = await walletServiceInstance.get<IGetSettingsAvailablePaymentMethodsResponse>(
    'settings/available-payment-methods/withdrawal'
  );
  return response.data.data;
};

export const deleteSavedCard = async (cardId: string) => {
  const response = await walletServiceInstance.delete(`/wallets/cards/${cardId}`);
  return response.data;
};

type CreateFastLinkUrlResponse = {
  id: string;
  data: {
    fastlinkURL: string;
    username: string;
    token: string;
  };
};

export const createFastLinkUrl = async () => {
  const response = await walletServiceInstance.post<CreateFastLinkUrlResponse>(
    '/wallets/aeropay/fastlinkurl'
  );

  return response.data.data;
};

export const createBankAccountLink = async (payload: { userId: string; userPassword: string }) => {
  const response = await walletServiceInstance.post<{
    id: string;
    data: BankAccount;
  }>('/wallets/aeropay/link-account', payload);
  return response.data.data;
};

export const getBankAccounts = async () => {
  const response = await walletServiceInstance.get<{
    id: string;
    data: {
      bankAccounts: BankAccount[];
    };
  }>('/wallets/aeropay/bank-accounts');
  return response.data.data;
};

export const aeropayChallenge = async () => {
  const response = await walletServiceInstance.post('/wallets/aeropay/challenge');
  return response.data.data;
};
export const aeropayConfirmUser = async (payload: { code: string }) => {
  const response = await walletServiceInstance.post('/wallets/aeropay/confirm-user', payload);
  return response.data.data;
};

export const createAeropayDeposit = async (payload: {
  amount: number;
  bankAccountId: string;
  bonusId?: string;
}) => {
  const response = await walletServiceInstance.post<IPostWalletsDepositsResponse>(
    '/wallets/aeropay/deposits',
    payload
  );
  return response.data;
};

export const deleteAeropayAccount = async (payload: { bankAccountId: string }) => {
  const response = await walletServiceInstance.delete('/wallets/aeropay/account', {
    data: payload,
  });
  return response.data;
};

// ---- Crypto Deposit v2/v3 (direct address rail). New symbols only — never reuse whop* (v1 hosted checkout).

// Arrivals (GET /wallets/whop-crypto/deposits) stays on @Version(V2) — see CRYPTO_ADDRESSES_API_VERSION's
// comment in the interface file for why this must NOT be bumped alongside the addresses routes.
const cryptoV2Headers = { headers: { [CRYPTO_V2_API_VERSION_HEADER]: CRYPTO_V2_API_VERSION } };
// EDGE-32059: the deposit-addresses GET and the new create-addresses POST move to @Version('3').
const cryptoAddressesHeaders = {
  headers: { [CRYPTO_V2_API_VERSION_HEADER]: CRYPTO_ADDRESSES_API_VERSION },
};

/** Permanent deposit addresses for the current user. Requires a FRESH GeoComply token (interceptor row). */
export const getCryptoDepositAddresses = async (): Promise<ICryptoDepositAddresses> => {
  const response = await walletServiceInstance.get<unknown>(
    '/wallets/whop-crypto/addresses',
    cryptoAddressesHeaders
  );
  return parseCryptoDepositAddresses(response.data);
};

// CryptoCooldownError now lives in ./interfaces/crypto-direct.interface (this file is capped at
// one class — max-classes-per-file — and VoucherNotValidError below already claims that slot).
// Re-exported so existing `from '~/services/wallet/wallet.service.api'` imports keep resolving.
export { CryptoCooldownError };

// Reuse the SAME strict check the addresses schema/GET applies to retryAfterAt
// (crypto-direct.schema.ts) so both wire paths agree on the same value: a non-strict-ISO string
// (e.g. "2026", which `new Date()` would happily accept) degrades to null rather than passing
// through. Keeps the null-return contract — any non-string / unparseable input lands in .catch(null).
const parseCooldownRetryAfterAt = (value: unknown): string | null =>
  z.string().datetime().nullable().catch(null).parse(value);

/** Provisions (or re-fetches) deposit addresses for the current user. Same schema as the GET.
 *  Bounded by a per-call `timeout` (CRYPTO_CREATE_TIMEOUT_MS) so the sheet's loading/settingUp lock
 *  can never hang forever, and accepts an optional `AbortSignal` so the sheet's 60s cycle deadline
 *  can cancel an in-flight poll. Both are merged into a FRESH config object — never mutate the shared
 *  `cryptoAddressesHeaders` const (the GET reuses it, and must stay timeout/signal-free). */
export const createCryptoDepositAddresses = async (
  signal?: AbortSignal
): Promise<ICryptoDepositAddresses> => {
  try {
    const response = await walletServiceInstance.post<unknown>(
      '/wallets/whop-crypto/addresses',
      undefined,
      { ...cryptoAddressesHeaders, timeout: CRYPTO_CREATE_TIMEOUT_MS, signal }
    );
    return parseCryptoDepositAddresses(response.data);
  } catch (error) {
    const response = (error as { response?: { status?: number; data?: unknown } })?.response;
    if (
      response?.status === 429 &&
      (response.data as { error?: string })?.error === 'WHOP_CRYPTO_PROVISIONING_COOLDOWN'
    ) {
      const retryAfterAt = (response.data as { data?: { retryAfterAt?: unknown } })?.data
        ?.retryAfterAt;
      throw new CryptoCooldownError(parseCooldownRetryAfterAt(retryAfterAt));
    }
    throw error;
  }
};

export type GetCryptoDepositsParams = {
  /** Opaque cursor from the previous page's `nextSince`. Omit on the first page. */
  since?: string;
  /** 1..50, server-enforced. */
  limit?: number;
};

export const getCryptoDeposits = async ({
  since,
  limit = 50,
}: GetCryptoDepositsParams): Promise<ICryptoDepositArrivals> => {
  const params = new URLSearchParams();
  if (since) params.set('since', since);
  params.set('limit', String(limit));

  const response = await walletServiceInstance.get<unknown>(
    `/wallets/whop-crypto/deposits?${params.toString()}`,
    cryptoV2Headers
  );
  return parseCryptoDepositArrivals(response.data);
};

export const postWalletRefund = async (payload: IPostWalletsRefundsPayload) => {
  const response = await walletServiceInstance.post<IPostWalletsRefundsResponse>(
    '/wallets/refunds',
    payload
  );
  return response.data;
};

// Voucher API methods

/**
 * Get all vouchers for the authenticated user
 * @returns Promise<IGetVouchersResponse> Array of user vouchers
 */
export const getVouchers = async (params?: {
  page?: number;
  pageSize?: number;
  status?: 'ACTIVE' | 'EXPIRED';
}): Promise<IGetVouchersResponse> => {
  const queryParams = new URLSearchParams(
    objectToTuples({
      ...(params?.page !== undefined && { page: params.page }),
      ...(params?.pageSize !== undefined && { pageSize: params.pageSize }),
      ...(params?.status !== undefined && { status: params.status }),
    })
  ).toString();

  const url = queryParams ? `/wallets/vouchers?${queryParams}` : '/wallets/vouchers';
  const response = await walletServiceInstance.get<IGetVouchersResponse>(url);
  return response.data;
};

/**
 * Get vouchers eligible for a specific contest or context
 * @param contestId Optional contest ID to filter eligible vouchers
 * @param voucherType Optional voucher type filter
 * @param amount Optional amount filter
 * @returns Promise<VoucherResponseDto[]> Array of eligible vouchers
 */
export const getEligibleVouchers = async (
  contestId?: string,
  voucherType?: string,
  amount?: number
): Promise<VoucherResponseDto[]> => {
  const params: Record<string, string | number> = {};

  if (contestId) params.contestId = contestId;
  if (voucherType) params.voucherType = voucherType;
  if (amount) params.amount = amount;

  const response = await walletServiceInstance.get<{ data: VoucherResponseDto[] }>(
    '/wallets/vouchers/eligible',
    {
      params,
    }
  );
  return response.data.data;
};

/**
 * Request payload for voucher validation
 */
export type ValidateVoucherPayload = {
  voucherId: string; // Path parameter: {id} in /wallets/vouchers/{id}/validate
  payload: {
    voucherType: string; // Required - Type of voucher (e.g., "QUICKPICKS")
    amount: number; // Required - Entry amount in DOLLARS (e.g., 25.5 for $25.50)
    entryType?: VoucherEntryType; // Optional - Entry context: backend gates voucher types per entry type
    contestId?: string; // Optional - Contest UUID
    league?: string; // Optional - League name (e.g., "NBA")
    contestType?: string; // Optional - Contest type (e.g., "team_brackets")
    // NOTE: prop_ids is NOT included - voucher validation doesn't require picks
  };
};

/**
 * Success response structure (200 OK)
 * Wrapped in SingleResult pattern.
 * Note: a 200 can still carry `isValid: false` with a populated `reason`
 * (e.g. voucher not allowed for the entry type) — see `validateVoucher`.
 */
export type APIValidateVoucherSuccessResponse = {
  data: {
    isValid: boolean;
    reason: string | null; // Populated when isValid is false
  };
};

/**
 * Thrown when the `/validate` endpoint resolves with `isValid: false` on a 200
 * response. Lets the catch-based call sites surface the backend `reason`
 * uniformly with the HTTP-400 rejection path.
 */
export class VoucherNotValidError extends Error {
  constructor(public reason: string) {
    super(reason);
    this.name = 'VoucherNotValidError';
  }
}

/**
 * Error response structure (400 - Validation Failure)
 */
export type APIValidateVoucherValidationErrorResponse = {
  id: string;
  statusCode: 400;
  message: string; // e.g., "Voucher is not valid for this contest"
  error: 'INVALID_VOUCHER_VALIDATION';
  stack?: string; // Only in non-production
};

/**
 * Error response structure (401 - Unauthorized)
 */
export type APIValidateVoucherUnauthorizedErrorResponse = {
  id: string;
  statusCode: 401;
  message: string;
  stack?: string;
};

export type APIValidateVoucherResponse =
  | APIValidateVoucherSuccessResponse
  | APIValidateVoucherValidationErrorResponse
  | APIValidateVoucherUnauthorizedErrorResponse;

/**
 * Validate that a voucher can be applied to the current entry
 *
 * @param payload Validation payload with voucher ID and context
 * @param payload.voucherId - The voucher ID (path parameter {id})
 * @param payload.payload.voucherType - Voucher type (e.g., "QUICKPICKS")
 * @param payload.payload.amount - Entry amount in DOLLARS (e.g., 25.5 for $25.50)
 * @param payload.payload.contestId - Optional contest UUID
 * @param payload.payload.league - Optional league name
 * @param payload.payload.contestType - Optional contest type
 * @returns Promise<APIValidateVoucherResponse> Validation result
 *
 * @example
 * ```typescript
 * await validateVoucher({
 *   voucherId: "43a4303e-f292-4f12-96f4-8b0c9eb4ef1e",
 *   payload: {
 *     voucherType: "QUICKPICKS",
 *     amount: 25.5,
 *     league: "NBA"
 *   }
 * });
 * ```
 */
export const validateVoucher = async ({
  voucherId,
  payload,
}: ValidateVoucherPayload): Promise<APIValidateVoucherResponse> => {
  const response = await walletServiceInstance.post<APIValidateVoucherSuccessResponse>(
    `/wallets/vouchers/${voucherId}/validate`,
    payload
  );
  // A 200 can still reject the voucher (isValid: false). Normalize that into a
  // throw so both call sites surface the backend `reason` via their catch path.
  const result = response.data?.data;
  if (result && result.isValid === false) {
    throw new VoucherNotValidError(result.reason ?? 'Voucher not allowed for this entry type');
  }
  return response.data;
};
